<script setup>
import BasePreferenceTemplate from '@/components/preference/templates/BasePreferenceTemplate.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'
import ErrorPopUp from '@/components/popups/ErrorPopUp.vue'
import PreferencesSaved from '@/components/popups/PreferencesSaved.vue'
import AutoSaveStatus from '@/components/preference/elements/AutoSaveStatus.vue'

import { withProps } from '@/components/preference/templates/preferenceTemplate.js'

defineProps({ ...withProps() })
defineEmits(['save', 'userEmail', 'update'])
defineOptions({ inheritAttrs: false })
</script>

<template>
  <BasePreferenceTemplate v-bind="{ ...$props, ...$attrs }" @save="$emit('save')" @user-email="$emit('userEmail', $event)" @update="$emit('update')">
    <template #form-step="{ logo, attrs, name, chosenEmail, updateChosenEmail, alertMsg, setEmail }">
      <PreferenceLogo :url="logo" />
      <div class="mx-6 text-start">
        <h1 class="text-xl font-medium mt-8">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h1>
        <h2 class="text-gray-400">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</h2>
      </div>

      <form class="text-center mt-5" @submit="setEmail($event, chosenEmail)">
        <div class="flex mx-6 text-start items-center">
          <div class="mb-6 w-full">
            <h1 class="text-lg font-medium">{{ $t('pref_page_email_form.form_title') }}</h1>
            <input
              id="email"
              :value="chosenEmail"
              type="email"
              name="email"
              class="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              :placeholder="$t('pref_page_email_form.form_placeholder').replace('[at]', '@')"
              @input="updateChosenEmail($event.target.value)"
            />
          </div>
          <Transition name="slide-fade">
            <ErrorPopUp v-if="alertMsg" :message="alertMsg" class="absolute left-[110%] z-10 flex items-center w-[180px] h-[40px]" />
          </Transition>
        </div>

        <button
          type="submit"
          class="bg-primary rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-[90%]"
          :style="{ backgroundColor: attrs.primary_color }"
        >
          {{ $t('pref_page_email_form.form_save_btn') }}
        </button>
      </form>
    </template>

    <template #preferences-header="{ logo, attrs, name }">
      <PreferenceLogo :url="logo" />
      <div class="text-start ml-6">
        <h2 class="text-xl font-medium mt-8">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h2>
        <p class="text-left text-gray-400">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
      </div>
    </template>

    <template #save-feedback="{ saved, saveStatus, attrs, alertMsg: alertMessage }">
      <AutoSaveStatus v-if="attrs?.auto_save" :status="saveStatus" :color="attrs?.primary_color" />
      <Transition v-else name="slide-fade">
        <PreferencesSaved v-if="saved" :message="alertMessage" />
      </Transition>
    </template>

    <template #action-buttons="{ attrs, disabled, updateGlobal, savePreferences }">
      <footer class="m-8 flex justify-center space-x-5">
        <button
          v-if="attrs.unsubscribe_all"
          class="rounded-full px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-9/10"
          @click="!disabled ? updateGlobal() : null"
        >
          {{ $t('pref_page.unsubscribe_btn') }}
        </button>
        <button
          v-if="!attrs?.auto_save"
          class="rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm bg-[#009ad0] hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-9/10"
          :style="{ backgroundColor: attrs.primary_color }"
          @click="savePreferences"
        >
          {{ $t('pref_page.save_btn') }}
        </button>
      </footer>
    </template>
  </BasePreferenceTemplate>
</template>

<style scoped>
.ellipse {
  position: absolute;
  width: 40vw;
  height: 40vh;
  left: 45%;
  top: 45%;
  background: #d2f1ff;
  opacity: 0.2;
  filter: blur(200.745px);
  transform: rotate(-180deg);
  pointer-events: none;
  border-radius: 100%;
  overflow: hidden;
}
</style>
